import React, { useEffect, useMemo, useState } from "react";
import API_BASE_URL from "../../config";

function getToken() {
  return localStorage.getItem("token");
}

function displayValue(value) {
  if (value === null || value === undefined || value === "") return "Blank";
  return String(value);
}

function isBlankValue(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function getProfilePath(recordType, id) {
  if (recordType === "client") return `/client-profile/${id}`;
  return `/lead-profile/${id}`;
}

function getRecordLabel(recordType) {
  return recordType === "client" ? "Client" : "Lead";
}

function ProfileMergeModal({
  recordType,
  primaryId,
  primaryName,
  onClose
}) {
  const [step, setStep] = useState("search");
  const [query, setQuery] = useState("");
  const [records, setRecords] = useState([]);
  const [duplicateRecord, setDuplicateRecord] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedValues, setSelectedValues] = useState({});
  const [finalRecordType, setFinalRecordType] = useState(recordType || "client");
  const [confirmText, setConfirmText] = useState("");

  const [searching, setSearching] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState("");

  const finalLabel = getRecordLabel(finalRecordType);

  const fields = useMemo(() => {
    return Array.isArray(preview?.fields) ? preview.fields : [];
  }, [preview]);

  const conflictCount = useMemo(() => {
    return fields.filter((field) => field.requiresChoice).length;
  }, [fields]);

  useEffect(() => {
    if (!query || query.trim().length < 2) {
      setRecords([]);
      return;
    }

    const timeout = setTimeout(() => {
      searchDuplicates();
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  const searchDuplicates = async () => {
    try {
      setSearching(true);
      setError("");

      const token = getToken();

      const res = await fetch(
        `${API_BASE_URL}/api/profile-merge/search?recordType=all&query=${encodeURIComponent(query)}&excludeRecordType=${recordType}&excludeId=${primaryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to search profiles");
      }

      setRecords(Array.isArray(data.records) ? data.records : []);
    } catch (err) {
      console.error("Search merge profiles error:", err);
      setError(err.message || "Failed to search profiles");
      setRecords([]);
    } finally {
      setSearching(false);
    }
  };

  const loadPreview = async (duplicate) => {
    try {
      setDuplicateRecord(duplicate);
      setLoadingPreview(true);
      setError("");

      const token = getToken();

      const res = await fetch(
        `${API_BASE_URL}/api/profile-merge/preview?primaryRecordType=${recordType}&primaryId=${primaryId}&duplicateRecordType=${duplicate.recordType}&duplicateId=${duplicate.id}&finalRecordType=${finalRecordType}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load merge preview");
      }

      const defaults = {};

      (data.fields || []).forEach((field) => {
        defaults[field.key] = field.recommendedValue ?? "";
      });

      setPreview(data);
      setSelectedValues(defaults);
      setConfirmText("");
      setStep("review");
    } catch (err) {
      console.error("Load merge preview error:", err);
      setError(err.message || "Failed to load merge preview");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleSelectValue = (fieldKey, value) => {
    setSelectedValues((prev) => ({
      ...prev,
      [fieldKey]: value ?? ""
    }));
  };

  const handleConfirmMerge = async () => {
    try {
      if (confirmText.trim().toUpperCase() !== "MERGE") {
        alert("Type MERGE to confirm.");
        return;
      }

      if (!duplicateRecord) {
        alert("Please select a duplicate profile.");
        return;
      }

      setMerging(true);
      setError("");

      const token = getToken();

      const res = await fetch(`${API_BASE_URL}/api/profile-merge/confirm`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          primaryRecordType: recordType,
          primaryId,
          duplicateRecordType: duplicateRecord.recordType,
          duplicateId: duplicateRecord.id,
          finalRecordType,
          selectedValues
        })
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to merge profiles");
      }

      alert("Profiles merged successfully.");

      const finalId = data.finalRecordId || primaryId;
      const finalType = data.finalRecordType || finalRecordType;

      window.location.href = getProfilePath(finalType, finalId);
    } catch (err) {
      console.error("Confirm profile merge error:", err);
      setError(err.message || "Failed to merge profiles");
      alert(err.message || "Failed to merge profiles");
    } finally {
      setMerging(false);
    }
  };

  const renderTypeBadge = (type) => (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        borderRadius: "999px",
        padding: "3px 8px",
        background: type === "client" ? "#eef2ff" : "#ecfeff",
        color: type === "client" ? "#4338ca" : "#0369a1",
        fontSize: "10px",
        fontWeight: 900,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        whiteSpace: "nowrap"
      }}
    >
      {getRecordLabel(type)}
    </span>
  );

  const renderValueButton = (field, value, selectedValue) => {
    const selected = String(selectedValue ?? "") === String(value ?? "");
    const blank = isBlankValue(value);

    return (
      <button
        type="button"
        onClick={() => handleSelectValue(field.key, value)}
        title={displayValue(value)}
        style={{
          width: "100%",
          minHeight: "32px",
          border: selected ? "1px solid #4f46e5" : "1px solid #e2e8f0",
          borderRadius: "8px",
          background: selected ? "#eef2ff" : "#ffffff",
          color: blank ? "#94a3b8" : "#334155",
          padding: "6px 8px",
          textAlign: "left",
          cursor: "pointer",
          fontSize: "12px",
          lineHeight: 1.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis"
        }}
      >
        {displayValue(value)}
      </button>
    );
  };

  return (
    <div className="modal-overlay">
      <div
        className="modal-content"
        style={{
          width: "min(980px, 96vw)",
          maxHeight: "88vh",
          padding: 0,
          overflow: "hidden",
          borderRadius: "16px"
        }}
      >
        <div
          style={{
            padding: "16px 18px",
            borderBottom: "1px solid #e2e8f0",
            background: "#ffffff"
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "14px",
              alignItems: "flex-start"
            }}
          >
            <div>
              <p
                style={{
                  margin: "0 0 4px",
                  color: "#64748b",
                  fontSize: "11px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.08em"
                }}
              >
                Merge Profile
              </p>

              <h2
                style={{
                  margin: 0,
                  color: "#0f172a",
                  fontSize: "19px",
                  lineHeight: 1.2
                }}
              >
                Resolve Duplicate Records
              </h2>

              <p
                style={{
                  margin: "6px 0 0",
                  color: "#64748b",
                  fontSize: "13px",
                  lineHeight: 1.4,
                  maxWidth: "720px"
                }}
              >
                Search for a duplicate, choose the profile type to keep, then select the final field values.
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={merging}
              aria-label="Close"
              style={{
                border: "1px solid #e2e8f0",
                background: "#ffffff",
                color: "#64748b",
                borderRadius: "10px",
                width: "34px",
                height: "34px",
                cursor: merging ? "not-allowed" : "pointer",
                fontSize: "20px",
                lineHeight: "20px"
              }}
            >
              ×
            </button>
          </div>
        </div>

        <div
          style={{
            padding: "14px 18px",
            borderBottom: "1px solid #e2e8f0",
            background: "#f8fafc"
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(220px, 280px) minmax(0, 1fr)",
              gap: "10px",
              alignItems: "stretch"
            }}
          >
            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#ffffff",
                padding: "10px 12px",
                minWidth: 0
              }}
            >
              <div
                style={{
                  color: "#64748b",
                  fontSize: "10px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: "5px"
                }}
              >
                Starting Profile
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  minWidth: 0
                }}
              >
                {renderTypeBadge(recordType)}
                <strong
                  style={{
                    color: "#0f172a",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}
                  title={primaryName}
                >
                  {primaryName || `${getRecordLabel(recordType)} #${primaryId}`}
                </strong>
              </div>
            </div>

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#ffffff",
                padding: "10px 12px"
              }}
            >
              <div
                style={{
                  color: "#64748b",
                  fontSize: "10px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: "7px"
                }}
              >
                Keep As
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "8px"
                }}
              >
                <button
                  type="button"
                  onClick={() => setFinalRecordType("lead")}
                  disabled={merging}
                  className={finalRecordType === "lead" ? "sales-primary-btn" : "sales-secondary-btn"}
                  style={{
                    minHeight: "32px",
                    padding: "6px 10px",
                    fontSize: "12px"
                  }}
                >
                  Lead
                </button>

                <button
                  type="button"
                  onClick={() => setFinalRecordType("client")}
                  disabled={merging}
                  className={finalRecordType === "client" ? "sales-primary-btn" : "sales-secondary-btn"}
                  style={{
                    minHeight: "32px",
                    padding: "6px 10px",
                    fontSize: "12px"
                  }}
                >
                  Client
                </button>
              </div>
            </div>

            <div
              style={{
                border: "1px solid #e2e8f0",
                borderRadius: "12px",
                background: "#ffffff",
                padding: "10px 12px",
                minWidth: 0
              }}
            >
              <div
                style={{
                  color: "#64748b",
                  fontSize: "10px",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: "5px"
                }}
              >
                Duplicate Profile
              </div>

              {duplicateRecord ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: 0
                  }}
                >
                  {renderTypeBadge(duplicateRecord.recordType)}
                  <strong
                    style={{
                      color: "#0f172a",
                      fontSize: "13px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                    title={duplicateRecord.name}
                  >
                    {duplicateRecord.name}
                  </strong>
                </div>
              ) : (
                <span style={{ color: "#94a3b8", fontSize: "13px" }}>
                  None selected
                </span>
              )}
            </div>
          </div>
        </div>

        <div
          style={{
            padding: "16px 18px",
            overflow: "auto",
            maxHeight: "calc(88vh - 205px)",
            background: "#ffffff"
          }}
        >
          {error && (
            <div
              style={{
                border: "1px solid #fecaca",
                background: "#fff5f5",
                color: "#b91c1c",
                borderRadius: "12px",
                padding: "10px 12px",
                fontSize: "13px",
                marginBottom: "12px"
              }}
            >
              {error}
            </div>
          )}

          {step === "search" && (
            <>
              <div className="sales-field">
                <label>Find Duplicate Profile</label>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search leads and clients by name..."
                  autoFocus
                />
              </div>

              <div style={{ marginTop: "12px" }}>
                {searching ? (
                  <div className="profile-empty-card">Searching profiles...</div>
                ) : records.length > 0 ? (
                  <div
                    style={{
                      border: "1px solid #e2e8f0",
                      borderRadius: "12px",
                      overflow: "hidden"
                    }}
                  >
                    {records.map((record, index) => (
                      <button
                        key={`${record.recordType}-${record.id}`}
                        type="button"
                        onClick={() => loadPreview(record)}
                        disabled={loadingPreview}
                        style={{
                          width: "100%",
                          border: "none",
                          borderTop: index === 0 ? "none" : "1px solid #edf2f7",
                          background: "#ffffff",
                          padding: "10px 12px",
                          textAlign: "left",
                          cursor: loadingPreview ? "wait" : "pointer",
                          display: "grid",
                          gridTemplateColumns: "72px minmax(0, 1fr)",
                          gap: "10px",
                          alignItems: "center"
                        }}
                      >
                        {renderTypeBadge(record.recordType)}

                        <span style={{ minWidth: 0 }}>
                          <span
                            style={{
                              display: "block",
                              color: "#0f172a",
                              fontSize: "13px",
                              fontWeight: 900,
                              lineHeight: 1.2
                            }}
                          >
                            {record.name}
                          </span>

                          <span
                            style={{
                              display: "block",
                              marginTop: "3px",
                              color: "#64748b",
                              fontSize: "12px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {[record.email, record.phone, record.street_address, record.city, record.state]
                              .filter(Boolean)
                              .join(" • ") || `${getRecordLabel(record.recordType)} #${record.id}`}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                ) : query.trim().length >= 2 ? (
                  <div className="profile-empty-card">No matching profiles found.</div>
                ) : (
                  <div className="profile-empty-card">
                    Type at least 2 characters to search for a duplicate lead or client.
                  </div>
                )}
              </div>
            </>
          )}

          {step === "review" && preview && (
            <>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "12px",
                  marginBottom: "10px"
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      color: "#0f172a",
                      fontSize: "15px"
                    }}
                  >
                    Choose Final Values
                  </h3>

                  <p
                    style={{
                      margin: "4px 0 0",
                      color: "#64748b",
                      fontSize: "12px",
                      lineHeight: 1.4
                    }}
                  >
                    Select the value to keep for each field. You can also edit the final value directly.
                  </p>
                </div>

                <span
                  style={{
                    borderRadius: "999px",
                    padding: "5px 10px",
                    background: conflictCount > 0 ? "#fff7ed" : "#ecfdf5",
                    color: conflictCount > 0 ? "#c2410c" : "#047857",
                    fontSize: "11px",
                    fontWeight: 900,
                    whiteSpace: "nowrap"
                  }}
                >
                  {conflictCount} Conflict{conflictCount === 1 ? "" : "s"}
                </span>
              </div>

              <div
                style={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "135px minmax(145px, 1fr) minmax(145px, 1fr) minmax(165px, 1fr)",
                    gap: "8px",
                    padding: "8px 10px",
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                    color: "#475569",
                    fontSize: "10px",
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em"
                  }}
                >
                  <div>Field</div>
                  <div>Starting</div>
                  <div>Duplicate</div>
                  <div>Final</div>
                </div>

                {fields.map((field, index) => {
                  const primaryValue = field.primaryValue ?? "";
                  const duplicateValue = field.duplicateValue ?? "";
                  const selectedValue = selectedValues[field.key] ?? "";

                  return (
                    <div
                      key={field.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "135px minmax(145px, 1fr) minmax(145px, 1fr) minmax(165px, 1fr)",
                        gap: "8px",
                        alignItems: "center",
                        padding: "8px 10px",
                        borderTop: index === 0 ? "none" : "1px solid #edf2f7",
                        background: field.requiresChoice ? "#fffaf0" : "#ffffff"
                      }}
                    >
                      <div
                        style={{
                          color: "#0f172a",
                          fontSize: "12px",
                          fontWeight: 900,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}
                        title={field.label}
                      >
                        {field.label}
                      </div>

                      {renderValueButton(field, primaryValue, selectedValue)}
                      {renderValueButton(field, duplicateValue, selectedValue)}

                      <input
                        value={selectedValue ?? ""}
                        onChange={(e) => handleSelectValue(field.key, e.target.value)}
                        style={{
                          height: "32px",
                          fontSize: "12px"
                        }}
                      />
                    </div>
                  );
                })}
              </div>

              <div
                style={{
                  marginTop: "12px",
                  border: "1px solid #e2e8f0",
                  borderRadius: "12px",
                  background: "#f8fafc",
                  padding: "12px"
                }}
              >
                <div
                  style={{
                    color: "#0f172a",
                    fontSize: "13px",
                    fontWeight: 900,
                    marginBottom: "4px"
                  }}
                >
                  Confirm Merge
                </div>

                <p
                  style={{
                    margin: "0 0 10px",
                    color: "#64748b",
                    fontSize: "12px",
                    lineHeight: 1.4
                  }}
                >
                  Notes, tasks, opportunities, documents, tags, custom fields, and history will move to the final {finalLabel.toLowerCase()} profile. The merged profile will be hidden.
                </p>

                <div className="sales-field">
                  <label>Type MERGE to confirm</label>
                  <input
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value)}
                    placeholder="MERGE"
                  />
                </div>
              </div>
            </>
          )}
        </div>

        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid #e2e8f0",
            background: "#ffffff",
            display: "flex",
            justifyContent: "space-between",
            gap: "10px"
          }}
        >
          <button
            type="button"
            className="sales-secondary-btn"
            onClick={() => {
              if (step === "review") {
                setStep("search");
                setPreview(null);
                setDuplicateRecord(null);
                setConfirmText("");
              } else {
                onClose();
              }
            }}
            disabled={merging}
          >
            {step === "review" ? "Back" : "Cancel"}
          </button>

          {step === "review" && (
            <button
              type="button"
              className="sales-danger-btn"
              onClick={handleConfirmMerge}
              disabled={merging || confirmText.trim().toUpperCase() !== "MERGE"}
            >
              {merging ? "Merging..." : `Merge as ${finalLabel}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProfileMergeModal;